import { CheckInTicket, Ticket } from "@garther/interfaces";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useApiClients } from "./context";

export const ticketsKeys = {
  all: ["tickets"] as const,
  details: () => [...ticketsKeys.all, "detail"] as const,
  detail: (ticketId: string) => [...ticketsKeys.details(), ticketId] as const
};

export function useTicketsInvalidation() {
  const queryClient = useQueryClient();

  return {
    invalidateAll: () => queryClient.invalidateQueries({ queryKey: ticketsKeys.all }),
    invalidateTicket: (ticketId: string) =>
      queryClient.invalidateQueries({ queryKey: ticketsKeys.detail(ticketId) })
  };
}

export function useTicket(options: { ticketId: string; enabled?: boolean }) {
  const { ticketApiClient } = useApiClients();
  const { ticketId, enabled = true } = options;

  return useQuery({
    queryKey: ticketsKeys.detail(ticketId),
    queryFn: () => ticketApiClient.getTicketById(ticketId),
    enabled: enabled && !!ticketId,
    staleTime: 5 * 60 * 1000
  });
}

export function useCheckInTicketMutation(
  options: { onSuccess?: (result: Ticket) => void; onError?: (error: unknown) => void } = {}
) {
  const { ticketApiClient } = useApiClients();
  const invalidate = useTicketsInvalidation();
  return useMutation({
    mutationFn: (data: CheckInTicket) => ticketApiClient.checkIn(data),
    onSuccess: (result) => {
      invalidate.invalidateTicket(result.id);
      invalidate.invalidateAll();
      options.onSuccess?.(result);
    },
    onError: (error) => options.onError?.(error)
  });
}
